import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus, ListChecks, Activity, FileText, Image as ImageIcon,
  Loader2, ExternalLink, Copy, Check, Search, Folder, MoreHorizontal,
  Pencil, Trash2, BarChart3, X, ChevronDown, FileUp,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  myComponents, listFolders, listTags,
  deleteFaq, deletePixel, deleteRichtext, deleteGallery,
} from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const PORTAL_LABEL = { parent: "Parent", student: "Student", faculty: "Faculty", alumni: "Alumni" };
const KIND_META = {
  faq: { icon: ListChecks, label: "FAQ", color: "bg-[#0055FF] text-white" },
  ps_pixel: { icon: Activity, label: "PortalStudio Pixel", color: "bg-zinc-900 text-white" },
  ga_pixel: { icon: Activity, label: "GA4 Pixel", color: "bg-orange-500 text-white" },
  richtext: { icon: FileText, label: "Rich Text", color: "bg-amber-500 text-white" },
  gallery: { icon: ImageIcon, label: "Gallery", color: "bg-emerald-600 text-white" },
};
const DELETERS = {
  faq: deleteFaq, ps_pixel: deletePixel, ga_pixel: deletePixel,
  richtext: deleteRichtext, gallery: deleteGallery,
};

function CopyUrl({ url, testid }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setDone(true); toast.success("URL copied");
      setTimeout(() => setDone(false), 1500);
    } catch { toast.error("Copy failed"); }
  };
  return (
    <button onClick={copy} data-testid={testid}
      className="text-xs text-zinc-500 hover:text-zinc-950 inline-flex items-center gap-1">
      {done ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      {done ? "Copied" : "Copy URL"}
    </button>
  );
}

function ComponentCard({ c, onDelete, folderMap }) {
  const [menu, setMenu] = useState(false);
  const meta = KIND_META[c.kind];
  const Icon = meta.icon;
  const url = c.urls?.embed_url || "";
  const folderName = c.folder_id ? folderMap[c.folder_id]?.name : null;

  return (
    <div data-testid={`component-card-${c.id}`}
      className="border border-zinc-200 bg-white rounded-md p-5 hover:border-zinc-300 transition-all relative">
      <div className="flex items-start gap-3">
        <div className={`h-10 w-10 rounded-md grid place-items-center ${meta.color}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-400 flex items-center gap-2">
            {meta.label}
            {folderName ? (
              <span className="inline-flex items-center gap-1 text-zinc-500 normal-case tracking-normal">
                <Folder className="h-2.5 w-2.5" /> {folderName}
              </span>
            ) : null}
          </div>
          <div className="font-display text-base font-medium text-zinc-950 truncate">
            {c.title || c.name || "Untitled"}
          </div>
        </div>
        <div className="relative">
          <button
            onClick={() => setMenu((m) => !m)}
            data-testid={`card-menu-${c.id}`}
            className="h-7 w-7 grid place-items-center rounded hover:bg-zinc-100 text-zinc-500">
            <MoreHorizontal className="h-4 w-4" />
          </button>
          {menu ? (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenu(false)} />
              <div className="absolute right-0 top-8 w-44 bg-white border border-zinc-200 rounded-md shadow-md z-20 py-1 text-sm">
                <Link to={`/edit/${c.kind}/${c.id}`} onClick={() => setMenu(false)}
                      data-testid={`card-edit-${c.id}`}
                      className="flex items-center gap-2 px-3 py-2 hover:bg-zinc-50">
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </Link>
                {(c.kind === "ps_pixel" || c.kind === "ga_pixel") ? (
                  <Link to={`/pixel/${c.id}/analytics`} onClick={() => setMenu(false)}
                        data-testid={`card-analytics-${c.id}`}
                        className="flex items-center gap-2 px-3 py-2 hover:bg-zinc-50">
                    <BarChart3 className="h-3.5 w-3.5" /> Analytics
                  </Link>
                ) : null}
                <button
                  onClick={() => { setMenu(false); onDelete(c); }}
                  data-testid={`card-delete-${c.id}`}
                  className="w-full text-left flex items-center gap-2 px-3 py-2 hover:bg-red-50 text-red-600">
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>

      {(c.portals || []).length > 0 ? (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {c.portals.map(p => (
            <Badge key={p} variant="secondary" className="text-[10px] font-medium">{PORTAL_LABEL[p] || p}</Badge>
          ))}
        </div>
      ) : null}

      {(c.tags || []).length > 0 ? (
        <div className="flex flex-wrap gap-1 mt-2">
          {c.tags.map(t => <span key={t} className="text-[10px] font-mono text-zinc-500">#{t}</span>)}
        </div>
      ) : null}

      {c.notes ? (<p className="text-xs text-zinc-500 mt-3 line-clamp-2">{c.notes}</p>) : null}

      <div className="flex items-center justify-between mt-4 pt-4 border-t border-zinc-100">
        <a href={url} target="_blank" rel="noopener noreferrer"
           className="text-xs text-zinc-500 hover:text-zinc-950 inline-flex items-center gap-1">
          Open <ExternalLink className="h-3 w-3" />
        </a>
        <CopyUrl url={url} testid={`copy-url-${c.id}`} />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [folders, setFolders] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [folderId, setFolderId] = useState("all");
  const [tag, setTag] = useState("all");
  const [portal, setPortal] = useState("all");

  const folderMap = useMemo(() => {
    const m = {}; for (const f of folders) m[f.id] = f; return m;
  }, [folders]);

  const reload = () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (type !== "all") params.type = type === "hubspot_form" ? "hubspot_forms"
                                   : type === "gallery" ? "galleries"
                                   : type === "faq" ? "faqs"
                                   : type === "ps_pixel" ? "ps_pixels"
                                   : type === "ga_pixel" ? "ga_pixels"
                                   : type === "richtext" ? "richtexts" : undefined;
    if (folderId !== "all") params.folder_id = folderId;
    if (tag !== "all") params.tag = tag;
    if (portal !== "all") params.portal = portal;

    myComponents(params)
      .then(setData)
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    listFolders().then(r => setFolders(r.folders || [])).catch(() => {});
    listTags().then(r => setAllTags(r.tags || [])).catch(() => {});
  }, []);
  useEffect(reload, [q, type, folderId, tag, portal]);

  const all = data ? [
    ...(data.faqs || []), ...(data.ps_pixels || []), ...(data.ga_pixels || []), ...(data.richtexts || []),
    ...(data.galleries || []),
  ].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")) : [];

  const onDelete = async (c) => {
    if (!window.confirm(`Delete ${c.title || c.name || "this component"}? Cannot be undone.`)) return;
    try {
      await DELETERS[c.kind](c.id);
      toast.success("Deleted");
      reload();
    } catch { toast.error("Delete failed"); }
  };

  const clearFilters = () => { setQ(""); setType("all"); setFolderId("all"); setTag("all"); setPortal("all"); };
  const hasFilters = q || type !== "all" || folderId !== "all" || tag !== "all" || portal !== "all";

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-3">
              {user?.customer?.name || (user?.role === "sysadmin" ? "All Tenants" : "My School")}
            </div>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight font-semibold text-zinc-950">Components</h1>
            <p className="text-zinc-600 mt-2 text-sm">Create, filter, edit and delete your embeddable components.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link to="/create/faq" data-testid="dashboard-create-faq">
              <Button variant="outline" className="rounded-md"><ListChecks className="h-4 w-4 mr-2" /> FAQ</Button>
            </Link>
            <Link to="/create/richtext" data-testid="dashboard-create-richtext">
              <Button variant="outline" className="rounded-md"><FileText className="h-4 w-4 mr-2" /> Rich Text</Button>
            </Link>
            <Link to="/create/gallery" data-testid="dashboard-create-gallery">
              <Button variant="outline" className="rounded-md"><ImageIcon className="h-4 w-4 mr-2" /> Gallery</Button>
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-md"
                        data-testid="dashboard-create-ps-pixel">
                  <Activity className="h-4 w-4 mr-2" /> PS Pixel
                  <ChevronDown className="h-3.5 w-3.5 ml-1.5 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel className="text-[10px] tracking-[0.2em] uppercase text-zinc-400">
                  PortalStudio tracking pixel
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild data-testid="dashboard-create-ps-pixel-single">
                  <Link to="/create/ps_pixel" className="cursor-pointer">
                    <Plus className="h-4 w-4 mr-2 text-zinc-500" />
                    <div className="flex flex-col">
                      <span className="text-[13px] text-zinc-950">Single component</span>
                      <span className="text-[11px] text-zinc-500">One tracking pixel for a specific page</span>
                    </div>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="dashboard-create-ps-pixel-bulk">
                  <Link to="/create/ps_pixel/bulk" className="cursor-pointer">
                    <FileUp className="h-4 w-4 mr-2 text-zinc-500" />
                    <div className="flex flex-col">
                      <span className="text-[13px] text-zinc-950">Upload list of pages</span>
                      <span className="text-[11px] text-zinc-500">One pixel per page, keyed by Portal Screen ID</span>
                    </div>
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Link to="/create/ga_pixel" data-testid="dashboard-create-ga-pixel">
              <Button variant="outline" className="rounded-md"><Activity className="h-4 w-4 mr-2 text-orange-500" /> GA4 Pixel</Button>
            </Link>
          </div>
        </div>

        {/* Filters */}
        <div className="border border-zinc-200 bg-white rounded-md p-4 mb-6 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="h-4 w-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search title, description, notes…"
              className="pl-9 h-9"
              data-testid="dashboard-search" />
          </div>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-[140px] h-9" data-testid="filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="faq">FAQ</SelectItem>
              <SelectItem value="richtext">Rich Text</SelectItem>
              <SelectItem value="gallery">Gallery</SelectItem>
              <SelectItem value="hubspot_form">HubSpot</SelectItem>
              <SelectItem value="pixel">Pixel</SelectItem>
            </SelectContent>
          </Select>
          <Select value={folderId} onValueChange={setFolderId}>
            <SelectTrigger className="w-[160px] h-9" data-testid="filter-folder"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All folders</SelectItem>
              <SelectItem value="none">No folder</SelectItem>
              {folders.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={tag} onValueChange={setTag}>
            <SelectTrigger className="w-[140px] h-9" data-testid="filter-tag"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All tags</SelectItem>
              {allTags.map(t => <SelectItem key={t} value={t}>#{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={portal} onValueChange={setPortal}>
            <SelectTrigger className="w-[140px] h-9" data-testid="filter-portal"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All portals</SelectItem>
              <SelectItem value="parent">Parent</SelectItem>
              <SelectItem value="student">Student</SelectItem>
              <SelectItem value="faculty">Faculty</SelectItem>
              <SelectItem value="alumni">Alumni</SelectItem>
            </SelectContent>
          </Select>
          {hasFilters ? (
            <button onClick={clearFilters} data-testid="clear-filters"
              className="h-9 px-3 rounded-md text-xs text-zinc-500 hover:text-zinc-950 inline-flex items-center gap-1">
              <X className="h-3 w-3" /> Clear
            </button>
          ) : null}
          <div className="ml-auto text-xs text-zinc-400">{all.length} result{all.length === 1 ? "" : "s"}</div>
        </div>

        {loading ? (
          <div className="h-40 grid place-items-center text-zinc-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : all.length === 0 ? (
          <div className="border border-dashed border-zinc-300 rounded-md p-10 text-center bg-white" data-testid="dashboard-empty">
            <div className="font-display text-lg font-medium text-zinc-950">
              {hasFilters ? "Nothing matches those filters" : "No components yet"}
            </div>
            <p className="text-sm text-zinc-500 mt-1">
              {hasFilters ? "Try clearing some filters." : "Create your first component to get an embeddable URL."}
            </p>
            {!hasFilters ? (
              <Link to="/create/richtext" className="inline-block mt-5">
                <Button className="rounded-md bg-zinc-950 text-white hover:bg-zinc-800">
                  <Plus className="h-4 w-4 mr-2" /> Create your first component
                </Button>
              </Link>
            ) : null}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {all.map((c) => <ComponentCard key={`${c.kind}-${c.id}`} c={c} onDelete={onDelete} folderMap={folderMap} />)}
          </div>
        )}
      </div>
    </div>
  );
}
